import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { LookupService } from '../../../../../_shared/services/lookup.service';
import { Option } from '../../../models/option.model';
import { PartDetails } from '../../../models/part-details.model';
import { UpdatePartRequest } from '../../../models/update-part-request.model';
import { PartsAndInventoryService } from '../../../services/parts-and-inventory.service';

@Component({
  selector: 'app-edit-part',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgbModule, TranslocoDirective, TranslocoPipe],
  templateUrl: './edit-part.component.html',
  styleUrl: './edit-part.component.scss'
})
export class EditPartComponent implements OnInit {
  @Input() partId!: number;

  form: FormGroup;
  isLoading = false;
  isPartLoading = false;
  isCategoriesLoading = false;
  isStorageLocationsLoading = false;

  categoryOptions: Option[] = [];
  storageLocationOptions: Option[] = [];

  reorderPointOptions: Option[] = [
    { label: '5', value: '5' },
    { label: '10', value: '10' },
    { label: '15', value: '15' }
  ];
  reorderQuantityOptions: Option[] = [
    { label: '10', value: '10' },
    { label: '25', value: '25' },
    { label: '50', value: '50' }
  ];
  maximumQuantityOptions: Option[] = [
    { label: '25', value: '25' },
    { label: '50', value: '50' },
    { label: '100', value: '100' }
  ];

  private partDetails: PartDetails | null = null;

  private lookupService = inject(LookupService);
  private partsAndInventoryService = inject(PartsAndInventoryService);
  private errorService = inject(ErrorHandlerService);
  private toastr = inject(ToastrService);
  private activeModal = inject(NgbActiveModal);
  private fb = inject(FormBuilder);
  private transloco = inject(TranslocoService);

  constructor() {
    this.form = this.fb.group({
      partName: [{ value: '', disabled: true }, Validators.required],
      partNumber: [{ value: '', disabled: true }, Validators.required],
      description: [''],
      partType: [{ value: '', disabled: true }, Validators.required],
      category: [{ value: '', disabled: true }, Validators.required],
      storageLocation: ['', Validators.required],
      reorderPoint: ['5', Validators.required],
      reorderQuantity: ['25', Validators.required],
      maximumQuantity: ['25', Validators.required]
    });
  }

  async ngOnInit(): Promise<void> {
    if (!Number.isFinite(this.partId)) {
      this.errorService.handleError('Invalid part id');
      this.closeModal();
      return;
    }

    await Promise.all([
      this.loadPartDetails(),
      this.loadPartCategories(),
      this.loadStorageLocations()
    ]);

    this.applyPartDetailsToForm();
  }

  get partTypeLabel(): string {
    const value = String(this.form.get('partType')?.value ?? '');
    if (!value && this.isPartLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingPartType');
    }
    return value || this.transloco.translate('parts.modals.dropdown.selectPartType');
  }

  get categoryLabel(): string {
    const selectedCategory = String(this.form.get('category')?.value ?? '');
    if (!selectedCategory && this.isCategoriesLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingCategories');
    }
    return this.categoryOptions.find(option => option.value === selectedCategory)?.label || this.transloco.translate('parts.modals.dropdown.selectCategory');
  }

  get storageLocationLabel(): string {
    const selectedLocation = String(this.form.get('storageLocation')?.value ?? '');
    if (!selectedLocation && this.isStorageLocationsLoading) {
      return this.transloco.translate('parts.modals.dropdown.loadingStorageLocations');
    }
    return this.storageLocationOptions.find(option => option.value === selectedLocation)?.label || this.transloco.translate('parts.modals.dropdown.selectStorageLocation');
  }

  get isInputLoading(): boolean {
    return this.isPartLoading || this.isCategoriesLoading || this.isStorageLocationsLoading;
  }

  onSelect(field: string, value: string, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.form.get(field)?.setValue(value);
    this.form.get(field)?.markAsTouched();
  }

  closeModal(): void {
    this.activeModal.close();
  }

  onCancel(): void {
    this.closeModal();
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || this.isInputLoading) {
      Object.keys(this.form.controls).forEach(key => this.form.get(key)?.markAsTouched());
      return;
    }

    const request = this.buildUpdatePartRequest();
    if (!request) {
      return;
    }

    this.isLoading = true;
    try {
      await this.partsAndInventoryService.updatePartAsync(this.partId, request);
      this.toastr.success(this.transloco.translate('parts.modals.editPart.toastUpdated'));
      this.activeModal.close(true);

    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isLoading = false;
    }
  }

  private async loadPartDetails(): Promise<void> {
    this.isPartLoading = true;
    try {
      const response = await this.partsAndInventoryService.getPartByIdAsync(this.partId);
      this.partDetails = response.data ?? null;
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isPartLoading = false;
    }
  }

  private async loadPartCategories(): Promise<void> {
    this.isCategoriesLoading = true;
    try {
      const response = await this.lookupService.getPartCategoriesAsync();
      this.categoryOptions = this.mapLookupItemsToOptions(response.data);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isCategoriesLoading = false;
    }
  }

  private async loadStorageLocations(): Promise<void> {
    this.isStorageLocationsLoading = true;
    try {
      const response = await this.lookupService.getWarehousesAsync([]);
      this.storageLocationOptions = this.mapLookupItemsToOptions(response.data);
    } catch (error) {
      this.errorService.handleError(error);
    } finally {
      this.isStorageLocationsLoading = false;
    }
  }

  private applyPartDetailsToForm(): void {
    if (!this.partDetails) {
      return;
    }

    this.form.patchValue({
      partName: this.partDetails.partName ?? '',
      partNumber: this.partDetails.partNumber ?? '',
      description: this.partDetails.description ?? '',
      partType: this.partDetails.partType?.name ?? '',
      category: String(this.partDetails.category?.id ?? ''),
      storageLocation: String(this.partDetails.warehouse?.id ?? ''),
      reorderPoint: String(this.partDetails.reorderPoint ?? ''),
      reorderQuantity: String(this.partDetails.reorderQuantity ?? ''),
      maximumQuantity: String(this.partDetails.maximumQuantity ?? '')
    });
  }

  private mapLookupItemsToOptions(items: any): Option[] {
    if (!Array.isArray(items)) {
      return [];
    }

    return items
      .map(item => {
        const id = item?.id ?? item?.value;
        const label = item?.name ?? item?.label;

        return {
          value: String(id ?? ''),
          label: String(label ?? '')
        } as Option;
      })
      .filter(option => option.value !== '' && option.label !== '');
  }

  private buildUpdatePartRequest(): UpdatePartRequest | null {
    const partName = String(this.form.get('partName')?.value ?? '').trim();
    const partNumber = String(this.form.get('partNumber')?.value ?? '').trim();
    const description = String(this.form.get('description')?.value ?? '').trim();

    const categoryId = Number(this.form.get('category')?.value ?? this.partDetails?.category?.id);
    const warehouseId = Number(this.form.get('storageLocation')?.value);
    const reorderPoint = Number(this.form.get('reorderPoint')?.value);
    const reorderQuantity = Number(this.form.get('reorderQuantity')?.value);
    const maximumQuantity = Number(this.form.get('maximumQuantity')?.value);

    const numericValues = [categoryId, warehouseId, reorderPoint, reorderQuantity, maximumQuantity];
    if (numericValues.some(value => !Number.isFinite(value))) {
      return null;
    }

    return {
      partName,
      partNumber,
      description,
      categoryId,
      warehouseId,
      reorderPoint,
      reorderQuantity,
      maximumQuantity
    };
  }
}